import { api } from '@/api';
import { getCookieValue } from '@/api/cookie';
import type { ErrorHandlerFunction, LoginViewModel, PtwSessionDto } from '@/api/generator';
import { getSignInRedirect } from '@/router/utils';
import { useAccountStore } from '@/stores/accountStore.ts';
import { useDateTimeStore } from '@/stores/dateTimeStore';
import { useLicenseStore } from '@/stores/licenseStore.ts';
import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { usePermissionsStore } from './permissionsStore';
import { Router } from 'vue-router';
import { routerInstance } from '@/router/router';
import i18next from 'i18next';
import { useSystemStore } from './systemStore';
import { initAppInsights } from '@/telemetry/appInsights';

/**
 * Use this store for authentication related tasks and data
 */
export const useAuthenticationStore = (router: Router = routerInstance) =>
  defineStore('authentication', () => {
    const session = ref<PtwSessionDto>();
    const xsrfToken = ref('');

    const isAuthenticated = computed(() => Boolean(session.value?.userId));
    const authenticationFailure = ref(false);

    async function checkAuthentication() {
      // Needed, because otherwise we get an `Microsoft.AspNetCore.Antiforgery.DefaultAntiforgery` error
      // when not logged in and trying to log in
      await fetchXSRFToken();
      await fetchSession();
    }

    async function signIn(
      loginViewModel: LoginViewModel,
      errorHandlers: ErrorHandlerFunction[] = [],
    ) {
      authenticationFailure.value = false;
      const response = await api.account.login(
        {
          loginViewModel,
        },
        [
          ...errorHandlers,
          () => {
            authenticationFailure.value = true;
          },
        ],
      );

      if (response.success) {
        authenticationFailure.value = false;

        // system-data might have changed in between
        // (e.g. maintenance-mode was deactivated)
        await useSystemStore().fetchAllData();

        await fetchSession(true);
        await fetchXSRFToken();

        // Trigger cross-tab login event
        localStorage.setItem('login-event', Date.now().toString());
        localStorage.removeItem('login-event');

        await router.push(getSignInRedirect(router.currentRoute.value));

        // `fetchSession` was called with `isSignIn=true` because we need to defer `i18next.changeLanguage` after the routing.
        // Otherwise `i18next.changeLanguage` would trigger a rerender of the SignInView,
        // if we open up the page for the first time and the browser locale doesn't match the user profile locale.
        const accountStore = useAccountStore();
        await i18next.changeLanguage(accountStore.language);
      }

      return response;
    }

    async function fetchSession(isSignIn?: boolean) {
      const { result, raw } = await api.account.getSession(undefined, {
        redirect: 'error',
      });

      if (result) {
        const extractedServerDate = raw.headers.get('date');
        if (extractedServerDate) {
          useDateTimeStore().setServerTime(DateTime.fromHTTP(extractedServerDate));
        }

        // there is additional information on the session not related
        // to the user, so we need to always set it
        session.value = result;

        // only if a user is logged in
        if (result?.userId) {
          await usePermissionsStore().fetchMyPermissions();
          await useAccountStore().fetchAccountData(result.userId, isSignIn);
          await useLicenseStore().fetchLicenseData();
          initAppInsights();
        }
      }
    }

    async function signOut() {
      const { success } = await api.account.logout();
      if (success) {
        // Trigger cross-tab logout event
        localStorage.setItem('logout-event', Date.now().toString());
        localStorage.removeItem('logout-event');

        // we don't need to do the following actions, because we do a hard reload
        // - fetch new session
        // - fetch token
        // - pinia reset
      }
    }

    async function fetchXSRFToken() {
      const response = await fetch('/AntiForgery/SetCookie');
      if (response.ok) {
        const successful = (await response.json()).success;

        const newToken = getCookieValue('XSRF-TOKEN');
        if (successful && newToken) {
          xsrfToken.value = newToken;
        }
      } else {
        // if backend has been rebooted and user was and is still online or backend is offline
        // (should both not happen on productive)
        // make sure to call it only once
        const sessionKey = 'xsrfReloadedOnce';
        const alreadyReloaded = sessionStorage.getItem(sessionKey) === 'true';

        if (alreadyReloaded) {
          sessionStorage.removeItem(sessionKey);
        } else {
          sessionStorage.setItem(sessionKey, 'true');
          window.location.reload();
        }
      }
    }

    return {
      isAuthenticated,
      xsrfToken,
      signIn,
      signOut,
      checkAuthentication,
      session,
      authenticationFailure,
    };
  })();
